import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { formatTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Avatar, Button, Card, Eyebrow, Pill } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { usePermissions, useWorkspace } from '../../lib/session';
import { useConnections } from './connections';
import { findSettingsItem, mobileLabels, settingsGroups } from './settingsNav';
import styles from './SettingsFrame.module.css';

interface SavedState {
  savedAt: Date | null;
  markSaved: () => void;
}

const SavedContext = createContext<SavedState | null>(null);

/** "Gespeichert · 14:32" (Einstellungen 01): pages call `markSaved()` after a successful auto-save. */
export function useSavedPill(): SavedState {
  const value = useContext(SavedContext);
  if (value === null) throw new Error('useSavedPill() needs the SettingsFrame');
  return value;
}

/** The settings frame (Einstellungen 01): title row with the auto-save pill, 262 px navigation, content. */
export function SettingsFrame() {
  const mobile = useIsMobile();
  const location = useLocation();
  const permissions = usePermissions();
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const markSaved = useCallback(() => setSavedAt(new Date()), []);
  const saved = useMemo(() => ({ savedAt, markSaved }), [savedAt, markSaved]);
  const showOrganization = permissions.has('settings.organization');
  const groups = showOrganization ? settingsGroups : settingsGroups.filter((group) => group.key === 'mine');
  const isIndex = location.pathname === '/settings' || location.pathname === '/settings/';

  if (mobile) {
    return (
      <SavedContext.Provider value={saved}>
        <div className={styles.mobile}>
          {isIndex ? (
            <MobileIndex savedAt={savedAt} />
          ) : (
            <div className={styles.mobilePage}>
              <Outlet />
            </div>
          )}
        </div>
      </SavedContext.Provider>
    );
  }

  if (isIndex) return <Navigate to="/settings/profile" replace />;

  return (
    <SavedContext.Provider value={saved}>
      <div className={styles.frame}>
        <div className={styles.titleRow}>
          <h1 className={styles.pageTitle}>Einstellungen</h1>
          {savedAt ? (
            <Pill variant="success" size="large" dot className={styles.savedPill}>
              Gespeichert · {formatTime(savedAt)}
            </Pill>
          ) : null}
        </div>
        <div className={styles.grid}>
          <nav className={styles.nav} aria-label="Einstellungen">
            {groups.map((group) => (
              <div key={group.key} className={styles.navGroup}>
                <Eyebrow as="div" className={styles.navGroupLabel}>
                  {group.label}
                </Eyebrow>
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={({ isActive }) =>
                      clsx(
                        styles.navItem,
                        (isActive || item.also?.some((path) => location.pathname.startsWith(path))) &&
                          styles.navItemActive,
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
            <div className={styles.navNote}>
              Änderungen werden automatisch gespeichert. Der Zeitpunkt steht oben rechts.
            </div>
          </nav>
          <div className={styles.content}>
            <Outlet />
          </div>
        </div>
      </div>
    </SavedContext.Provider>
  );
}

/** Mobile settings overview (the list of EM1 reduced to what exists in the shell milestone). */
function MobileIndex({ savedAt }: { savedAt: Date | null }) {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const permissions = usePermissions();
  const name = `${workspace.user.firstName} ${workspace.user.lastName}`.trim();
  const mine = settingsGroups[0];
  const connections = useConnections();
  // EM1 „1 Hinweis": connections that need the user (re-login, error, disconnected).
  const hints = (connections.data?.items ?? []).filter(
    (item) =>
      item.status === 'credentials_required' || item.status === 'error' || item.status === 'disconnected',
  ).length;
  if (!mine) return null;
  return (
    <div className={styles.mobileIndex}>
      <button type="button" className={styles.profileCard} onClick={() => navigate('/settings/profile')}>
        <Avatar
          initials={workspace.user.initials}
          name={name}
          size={40}
          src={workspace.user.avatarUrl ?? undefined}
        />
        <span className={styles.profileText}>
          <span className={styles.profileName}>{name}</span>
          <span className={styles.profileSub}>
            {workspace.user.roleLabel} · {workspace.organizationName}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden="true">
          ›
        </span>
      </button>
      <div className={styles.mobileGroup}>
        <Eyebrow as="div" className={styles.mobileGroupLabel}>
          {mine.label}
        </Eyebrow>
        <div className={styles.mobileList}>
          {mine.items.map((item) => (
            <button
              key={item.key}
              type="button"
              className={styles.mobileItem}
              onClick={() => navigate(item.href)}
            >
              <span>{mobileLabels[item.key] ?? item.label}</span>
              {item.key === 'connections' && hints > 0 ? (
                <Pill variant="warning">{hints === 1 ? '1 Hinweis' : `${hints} Hinweise`}</Pill>
              ) : null}
              <span className={styles.chevron} aria-hidden="true">
                ›
              </span>
            </button>
          ))}
        </div>
      </div>
      {permissions.has('settings.organization') ? (
        <div className={styles.mobileGroup}>
          <Eyebrow as="div" className={styles.mobileGroupLabel}>
            Organisation
          </Eyebrow>
          <div className={styles.orgCard}>
            <span className={styles.orgTitle}>Marke, Domain, Telefonie, Nutzer</span>
            <span className={styles.orgSub}>Nur am Rechner bearbeitbar</span>
          </div>
        </div>
      ) : null}
      {savedAt ? (
        <Pill variant="success" size="large" dot className={styles.mobileSaved}>
          Alle Änderungen gespeichert
        </Pill>
      ) : null}
    </div>
  );
}

export interface SettingsPageHeaderProps {
  eyebrow: string;
  title: string;
  description?: ReactNode | undefined;
  /** Right-aligned controls in the header row (desktop). */
  actions?: ReactNode | undefined;
}

/** Page head of a settings page (Einstellungen 15/16): eyebrow, display title, one-line description. */
export function SettingsPageHeader({ eyebrow, title, description, actions }: SettingsPageHeaderProps) {
  const mobile = useIsMobile();
  if (mobile) {
    return description ? <p className={styles.mobileDescription}>{description}</p> : null;
  }
  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className={styles.title}>{title}</h2>
        {description ? <p className={styles.description}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.headerActions}>{actions}</div> : null}
    </div>
  );
}

/** Settings pages that arrive in a later milestone: the item's name with a note (no design for M2). */
export function SettingsEmptyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const found = findSettingsItem(location.pathname);
  const label = found?.item.label ?? 'Einstellungen';
  const group = found?.group.label ?? 'Meine Einstellungen';
  return (
    <div className={styles.page}>
      <SettingsPageHeader eyebrow={group} title={label} />
      <Card className={styles.emptyCard}>
        <div className={styles.emptyTitle}>Noch nicht verfügbar.</div>
        <div className={styles.emptyText}>
          {label} wird in einer späteren Stufe freigeschaltet. Benachrichtigungen und Sicherheit stehen
          bereits bereit.
        </div>
        <div className={styles.emptyActions}>
          <Button variant="secondary" size="small" onClick={() => navigate('/settings/notifications')}>
            Benachrichtigungen
          </Button>
          <Button variant="secondary" size="small" onClick={() => navigate('/settings/security')}>
            Sicherheit
          </Button>
        </div>
      </Card>
    </div>
  );
}
